import React, { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { apiService, AUTH_EXPIRED_EVENT, SESSION_EXPIRED_MESSAGE } from '@/services/apiService';

interface AuthContextType {
  isAuthenticated: boolean;
  userPhone: string | null;
  userId: string | null;
  userName: string | null;
  userRole: 'admin' | 'user' | null;
  isProfileUpdated: boolean;
  token: string | null;
  canAccessProducts: boolean;
  isAdmin: () => boolean;
  login: (token: string, userId: string, phoneNumber: string, isProfileUpdated: boolean, name: string | null, role: 'admin' | 'user', canAccessProducts?: boolean) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface StoredAuth {
  token: string | null;
  userId: string | null;
  userPhone: string | null;
  userName: string | null;
  userRole: 'admin' | 'user' | null;
  isProfileUpdated: boolean;
  canAccessProducts: boolean;
  isAuthenticated: boolean;
}

const AUTH_STORAGE_KEYS = [
  'authToken', 'userId', 'userPhone', 'userName', 'userRole',
  'isProfileUpdated', 'sessionExpiry', 'canAccessProducts',
];

const clearStoredAuth = () => AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));

const readStoredAuth = (): StoredAuth => {
  if (typeof window === 'undefined') {
    return {
      token: null, userId: null, userPhone: null, userName: null, userRole: null,
      isProfileUpdated: false, canAccessProducts: false, isAuthenticated: false,
    };
  }
  const storedToken = localStorage.getItem('authToken');
  const storedUserId = localStorage.getItem('userId');
  const storedPhone = localStorage.getItem('userPhone');
  const storedExpiry = localStorage.getItem('sessionExpiry');

  if (storedToken && storedUserId && storedPhone && storedExpiry) {
    const expiryTime = parseInt(storedExpiry, 10);
    if (Date.now() < expiryTime) {
      apiService.setToken(storedToken);
      return {
        token: storedToken,
        userId: storedUserId,
        userPhone: storedPhone,
        userName: localStorage.getItem('userName'),
        userRole: localStorage.getItem('userRole') as 'admin' | 'user' | null,
        isProfileUpdated: localStorage.getItem('isProfileUpdated') === 'true',
        canAccessProducts: localStorage.getItem('canAccessProducts') === 'true',
        isAuthenticated: true,
      };
    }
    clearStoredAuth();
  }
  return {
    token: null, userId: null, userPhone: null, userName: null, userRole: null,
    isProfileUpdated: false, canAccessProducts: false, isAuthenticated: false,
  };
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const initial = readStoredAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(initial.isAuthenticated);
  const [userPhone, setUserPhone] = useState<string | null>(initial.userPhone);
  const [userId, setUserId] = useState<string | null>(initial.userId);
  const [userName, setUserName] = useState<string | null>(initial.userName);
  const [userRole, setUserRole] = useState<'admin' | 'user' | null>(initial.userRole);
  const [isProfileUpdated, setIsProfileUpdated] = useState(initial.isProfileUpdated);
  const [token, setToken] = useState<string | null>(initial.token);
  const [canAccessProducts, setCanAccessProducts] = useState(initial.canAccessProducts);

  const login = (token: string, userId: string, phoneNumber: string, isProfileUpdated: boolean, name: string | null, role: 'admin' | 'user', canAccessProducts: boolean = false) => {
    const expiryTime = Date.now() + 30 * 24 * 60 * 60 * 1000; // 30 days
    
    setToken(token);
    setUserId(userId);
    setUserPhone(phoneNumber);
    setUserName(name);
    setUserRole(role);
    setIsProfileUpdated(isProfileUpdated);
    setCanAccessProducts(canAccessProducts);
    setIsAuthenticated(true);
    
    localStorage.setItem('authToken', token);
    localStorage.setItem('userId', userId);
    localStorage.setItem('userPhone', phoneNumber);
    localStorage.setItem('userName', name || '');
    localStorage.setItem('userRole', role);
    localStorage.setItem('isProfileUpdated', String(isProfileUpdated));
    localStorage.setItem('sessionExpiry', String(expiryTime));
    localStorage.setItem('canAccessProducts', String(canAccessProducts));
    
    apiService.setToken(token);
  };

  const clearSession = () => {
    setIsAuthenticated(false);
    setUserPhone(null);
    setUserId(null);
    setUserName(null);
    setUserRole(null);
    setIsProfileUpdated(false);
    setCanAccessProducts(false);
    setToken(null);

    clearStoredAuth();
    apiService.setToken(null);
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearSession();
    }
  };

  // The server can drop a session at any time, so the API layer tells us when a
  // request came back 401 and the stored token is no longer worth anything.
  useEffect(() => {
    const handleExpired = () => {
      if (!localStorage.getItem('authToken')) return;
      clearSession();
      toast.error(SESSION_EXPIRED_MESSAGE);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, handleExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleExpired);
  }, []);

  const isAdmin = () => userRole === 'admin';

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      userPhone,
      userId,
      userName,
      userRole,
      isProfileUpdated,
      token,
      canAccessProducts,
      isAdmin,
      login,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
